import { useState, useEffect } from "react";
import { Check, MapPin, MapPinHouse } from "lucide-react";
import { cn } from "@/lib/utils";
import { CommandInput, Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { InputWithIcon } from "@/components/ui/input";
import { api } from "@/lib/api";
import type { PlaceAutocompleteResult } from "nautika-types";
import { flag } from "country-emoji";
import { useGeoLocationInfo, useLanguageInfo } from "@/hooks/useGeoLocation";

// Fonction helper pour obtenir le drapeau emoji du pays
function getCountryFlag(countryName: string): string {
  const flagEmoji = flag(countryName);
  return flagEmoji || "🌍";
}

interface LocationAutocompleteProps {
  value?: {
    city: string;
    postalCode: string;
    country: string;
    timezone: string;
  };
  onValueChange: (value: { city: string; postalCode: string; country: string; timezone: string }) => void;
  className?: string;
  errors?: {
    postalCode?: string;
    city?: string;
  };
  isSubmitted?: boolean;
}

export function LocationAutocomplete({ value, onValueChange, className, errors, isSubmitted }: LocationAutocompleteProps) {
  const { data: geoInfo } = useGeoLocationInfo();
  const languageInfo = useLanguageInfo();
  const [postalCodeSuggestions, setPostalCodeSuggestions] = useState<PlaceAutocompleteResult[]>([]);
  const [postalCodeLoading, setPostalCodeLoading] = useState(false);
  const [postalCodeMenuOpen, setPostalCodeMenuOpen] = useState(false);
  const [showCityField, setShowCityField] = useState(false);
  const [cityInputValue, setCityInputValue] = useState(value?.city || "");
  const [postalCodeInputValue, setPostalCodeInputValue] = useState(value?.postalCode || "");
  const [isUserTyping, setIsUserTyping] = useState(false); // Pour différencier saisie utilisateur vs sélection
  const [apiError, setApiError] = useState(false); // Pour gérer les erreurs d'API
  const [postalCodeTouched, setPostalCodeTouched] = useState(false); // Pour savoir si le champ a été touché
  const [cityTouched, setCityTouched] = useState(false); // Pour savoir si le champ ville a été touché
  const [shouldFocusPostalCode, setShouldFocusPostalCode] = useState(false); // Pour contrôler l'auto-focus

  const { mutateAsync: searchPlaces } = api.useMutation("post", "/google/places/search");

  // Maintenir le focus quand on passe en mode erreur d'API
  useEffect(() => {
    if (apiError && shouldFocusPostalCode) {
      setShouldFocusPostalCode(false); // Reset pour éviter les re-focus multiples
    }
  }, [apiError, shouldFocusPostalCode]); // Search function for postal code only (city search removed since field is now read-only)
  useEffect(() => {
    // Ne rechercher que si l'utilisateur tape (pas lors des sélections automatiques)
    if (!isUserTyping) {
      return;
    }

    // Si l'API a déjà échoué, ne plus tenter de recherches pour cette session
    if (apiError) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      if (postalCodeInputValue && postalCodeInputValue.length >= 2) {
        setPostalCodeLoading(true);
        setPostalCodeMenuOpen(true);
        try {
          const results = await searchPlaces({
            body: {
              query: postalCodeInputValue,
              countryCode: geoInfo?.countryCode || "FR",
              language: languageInfo.language,
            },
          });
          setPostalCodeSuggestions(results || []);
          setApiError(false); // Reset error state on success
        } catch (error) {
          console.error("Erreur lors de la recherche code postal:", error);
          setPostalCodeSuggestions([]);
          setApiError(true); // Marquer l'erreur d'API
          setShouldFocusPostalCode(true); // Déclencher l'auto-focus
          setShowCityField(true); // Débloquer le champ ville pour saisie manuelle
        } finally {
          setPostalCodeLoading(false);
        }
      } else {
        setPostalCodeSuggestions([]);
        setPostalCodeMenuOpen(false);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [postalCodeInputValue, searchPlaces, isUserTyping, geoInfo?.countryCode, languageInfo.language, apiError]);

  const handlePostalCodeInput = (input: string) => {
    setPostalCodeInputValue(input);
    setIsUserTyping(true); // Marquer que l'utilisateur tape

    // Update form value immediately for manual typing
    onValueChange({
      city: value?.city || "",
      postalCode: input,
      country: value?.country || "",
      timezone: value?.timezone || "",
    });
  };

  const handlePostalCodeBlur = () => {
    setPostalCodeTouched(true); // Marquer comme touché
    // Afficher le champ ville quand l'utilisateur perd le focus du code postal
    // seulement s'il y a une valeur dans le code postal OU s'il y a une erreur d'API
    if ((postalCodeInputValue && postalCodeInputValue.length > 0) || apiError) {
      setShowCityField(true);
    }
  };

  const handleCityInput = (input: string) => {
    setCityInputValue(input);
    // Mettre à jour la valeur du formulaire avec la ville saisie manuellement
    onValueChange({
      city: input,
      postalCode: value?.postalCode || postalCodeInputValue,
      country: value?.country || "",
      timezone: value?.timezone || "",
    });
  };

  const handleCityBlur = () => {
    setCityTouched(true); // Marquer comme touché
  };

  // Fonction helper pour déterminer si on doit afficher une erreur
  const shouldShowError = (fieldName: "postalCode" | "city", fieldTouched: boolean) => {
    if (isSubmitted) return true; // Toujours afficher après submit
    if (!fieldTouched) return false; // Ne pas afficher si pas encore touché

    // Afficher seulement si touché et qu'il y a une erreur
    const error = fieldName === "postalCode" ? errors?.postalCode : errors?.city;
    return !!error;
  };

  const handlePostalCodeSelect = (placeId: string) => {
    const selectedPlace = postalCodeSuggestions.find((place) => place.placeId === placeId);
    if (selectedPlace) {
      // Marquer qu'on ne tape plus (pour éviter de relancer la recherche)
      setIsUserTyping(false);
      // Réinitialiser l'état d'erreur puisqu'on a une sélection valide
      setApiError(false);

      // Mettre à jour les valeurs locales - afficher displayCity mais stocker city
      setCityInputValue(selectedPlace.displayCity); // Affichage avec arrondissement
      setPostalCodeInputValue(selectedPlace.postalCode);

      // Informer le parent - stocker city seul en BDD
      onValueChange({
        city: selectedPlace.city, // Ville seule pour la BDD
        postalCode: selectedPlace.postalCode,
        country: selectedPlace.country,
        timezone: selectedPlace.timezone,
      });

      // Fermer les menus et vider les suggestions
      setPostalCodeSuggestions([]);
      setPostalCodeMenuOpen(false);

      setTimeout(() => {
        // Afficher le champ ville après sélection
        setShowCityField(true);
      }, 1000);
    }
  };
  return (
    <div className={cn("relative flex transition-all duration-1000 ease-in-out", showCityField ? "gap-3" : "gap-0", className)}>
      {/* Champ Code Postal - prend toute la largeur quand la ville n'est pas visible */}
      <div className={cn("relative w-full flex-1 transition-all duration-1000 ease-in-out")}>
        {apiError ? (
          // Fallback avec InputWithIcon en cas d'erreur d'API
          <div>
            <InputWithIcon
              placeholder="Code postal"
              value={postalCodeInputValue}
              onChange={(e) => handlePostalCodeInput(e.target.value)}
              onBlur={handlePostalCodeBlur}
              icon={MapPin}
              wrapperClassName="h-10 shadow-xs"
              autoFocus={shouldFocusPostalCode}
            />
            {shouldShowError("postalCode", postalCodeTouched) && errors?.postalCode && <div className="mt-1 text-sm text-red-500">{errors.postalCode}</div>}
          </div>
        ) : (
          // Interface normale avec Command et autocomplete
          <div>
            <Command className="overflow-visible rounded-lg border bg-white shadow-xs" shouldFilter={false}>
              <CommandInput
                placeholder="Code postal"
                value={postalCodeInputValue}
                borderBottom={false}
                onValueChange={handlePostalCodeInput}
                onBlur={handlePostalCodeBlur}
                className="h-10"
              />
              {postalCodeMenuOpen && (postalCodeLoading || postalCodeSuggestions.length > 0) && (
                <CommandList
                  className="absolute top-full left-0 z-50 max-h-[300px] overflow-auto rounded-b-lg border border-t-0 bg-white shadow-lg"
                  style={{
                    width: showCityField ? "calc(200% + 0.75rem)" : "100%", // Se déploie sur toute la largeur du conteneur parent
                  }}
                >
                  {postalCodeLoading ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">Recherche en cours...</div>
                  ) : (
                    <>
                      <CommandEmpty>Aucun code postal trouvé</CommandEmpty>
                      <CommandGroup>
                        {postalCodeSuggestions.map((place) => (
                          <CommandItem
                            key={place.placeId}
                            value={place.placeId}
                            onSelect={handlePostalCodeSelect}
                            className="flex cursor-pointer items-start gap-3 py-3"
                          >
                            <MapPin className="mt-1 h-4 w-4 text-muted-foreground" />
                            <div className="flex-1">
                              <div className="text-sm font-medium">
                                {place.postalCode} - {place.displayCity}
                              </div>
                              {place.country !== "France" && (
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {getCountryFlag(place.country)} {place.country}
                                </div>
                              )}
                            </div>
                            {value?.city === place.city && value?.postalCode === place.postalCode && <Check className="mt-1 h-4 w-4 text-primary" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              )}
            </Command>
            {shouldShowError("postalCode", postalCodeTouched) && errors?.postalCode && <div className="mt-1 text-sm text-red-500">{errors.postalCode}</div>}
          </div>
        )}
      </div>

      {/* Champ Ville - apparaît avec une transition quand showCityField est true */}
      <div className={cn("!disabled:opacity-100 relative transition-all duration-300 ease-in-out", showCityField ? "flex-1 opacity-100" : "w-0 opacity-0")}>
        <div>
          <InputWithIcon
            placeholder="Ville"
            value={cityInputValue}
            onChange={(e) => handleCityInput(e.target.value)}
            onBlur={handleCityBlur}
            icon={MapPinHouse}
            disabled={!apiError} // Désactiver seulement si pas d'erreur d'API
            readOnly={!apiError} // Lecture seule seulement si pas d'erreur d'API
            wrapperClassName="h-10 shadow-xs has-[input:disabled]:opacity-100"
          />
          {shouldShowError("city", cityTouched) && errors?.city && <div className="mt-1 text-sm text-red-500">{errors.city}</div>}
        </div>
      </div>
    </div>
  );
}
